import { useMemo } from "react";
import {
  UneligibleTradeReason,
  TradeFailedParams,
  TradeMethod,
  TradeSubmittedParams,
  WidgetConfig,
  HubUserCredentials,
  METRICS_EVENTS,
  TradeExecutedPayload,
  TradeFailedPayload,
  FEATURE_FLAGS,
} from "@cede/types";
import { CEDE_CEXS, QueryKeys, getBaseToken } from "@cede/utils";
import { useCampaign } from "@/hooks/campaign/useCampaign";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { invalidateReferralEligibilityQueries } from "@/hooks/referral/referralQueryKeys";
import { useReferrals } from "@/hooks/referral/useReferrals";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useTradingViewStore } from "@/store/useTradingViewStore";
import { getCurrentEpochIndex } from "@/utils/campaign";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "next/navigation";

type UseTradeWidgetConfigProps = {
  access: boolean | undefined;
  reason: UneligibleTradeReason | undefined;
  isAccessLoading: boolean;
  defiAddresses: string[];
  nextAccessDate: Date | undefined;
  authenticated: boolean;
  auth: HubUserCredentials;
  logout: () => void;
  requiresSocialAccounts: boolean;
  hasSocialAccountsConnected: boolean;
  onConnectSocialAccounts: () => void;
  walletAgeReason?: string | null;
};

export type ITradingUneligibleData = {
  invalidAccessReason: UneligibleTradeReason;
  defiAddresses: string[];
  nextAccessDate: Date | undefined;
  hteCampaignId?: string;
  hteCampaignName?: string;
  hteCampaignExchangeIds?: string[];
  walletAgeReason?: string | null;
  referralOnlyExchangeIds?: string[];
  campaignName?: string;
  exchangeAccountId?: string;
  /** Campaign's target exchange IDs (used to name the exchange in blocked-access messages). */
  blockedExchangeIds?: string[];
};

export function useTradeWidgetConfig({
  access,
  reason,
  isAccessLoading,
  defiAddresses,
  nextAccessDate,
  authenticated,
  auth,
  logout,
  requiresSocialAccounts,
  hasSocialAccountsConnected,
  onConnectSocialAccounts,
  walletAgeReason,
}: UseTradeWidgetConfigProps) {
  const { hubUser } = useHubUser();
  const { refereeReferral } = useReferrals({ mode: "minimal", enabled: authenticated });

  const queryClient = useQueryClient();
  const { id: campaignName } = useParams<{ id: string }>();
  const { campaign: campaignMetadata, nextEpoch } = useCampaign((campaignName as string) || "");
  const currentEpochIndex = getCurrentEpochIndex(campaignMetadata?.epochs || []);
  const currentEpoch = campaignMetadata?.epochs?.[currentEpochIndex];
  // The trade controller derives the AccountSelector items, "not-connected"
  // exchanges and limit-order availability from this epoch's `exchangeIds` /
  // `limitOrder`. For a not-yet-started campaign there's no current epoch, so
  // fall back to the first upcoming one — otherwise the selector would
  // collapse to "all CEXes supported by Cede Hub" instead of the campaign's
  // allowed set.
  const effectiveEpoch = currentEpoch ?? nextEpoch ?? undefined;
  const { trackEvent } = useMetricsEvents();
  const isTradeExecutedReportingEnabled = useFeatureFlagIsOn(
    FEATURE_FLAGS.TRADE_REPORTING_API_TRADE_EXECUTED_EVENT_ENABLE,
  );
  // Read the user's currently-selected pair so multi-pair campaigns recommend
  // the active token rather than collapsing back to the campaign's primary
  // tokenSymbol on every render.
  const activePairSymbol = useTradingViewStore((s) => s.pairSymbol);

  const tradeWidgetConfig = useMemo(() => {
    if (authenticated && campaignMetadata && hubUser?.defiAddress && !isAccessLoading) {
      const queryParams = new URLSearchParams(window.location.search);
      const config: WidgetConfig = {
        tokenSymbols: queryParams.get("tokenSymbols")?.split(",") ?? undefined,
        defiAddress: hubUser?.defiAddress,
        isHub: true,
        auth,
        requiresSocialAccounts: requiresSocialAccounts && !hubUser?.isWhiteListed,
        hasSocialAccountsConnected,
        onConnectSocialAccounts,
      };
      config.onWalletDisconnect = () => {
        logout();
      };
      /**
       * @remarks
       * Use the exchangeIds from the active epoch (or the upcoming one when
       * the campaign hasn't started yet — see `effectiveEpoch`).
       */
      config.exchangeIds = effectiveEpoch?.exchangeIds || [];
      // Recommended token tracks the user's current pair selection so the trade
      // widget for multi-token campaigns surfaces the right token.
      const selectedBaseFromPair = activePairSymbol?.pairSymbol ? getBaseToken(activePairSymbol.pairSymbol) : undefined;
      config.recommendedToken = selectedBaseFromPair || campaignMetadata?.tokenSymbol;

      let limitOrderExchangeIds = config.exchangeIds?.filter((id) => id !== CEDE_CEXS.BYBIT) || [];

      /**
       * @remarks
       * We want to disable limit orders for all the exchanges if the campaign does not allow it
       */
      if (effectiveEpoch?.limitOrder === false) {
        limitOrderExchangeIds = [];
      }

      config.limitOrderExchangeIds = limitOrderExchangeIds;
      config.keepPreviousTradeValues = true;
      config.campaignId = campaignName as string;

      const referralProperties = {
        referrerId: refereeReferral?.referrerId,
        referralStatus: refereeReferral?.status,
      };

      config.onTradeSubmitted = async (params: TradeSubmittedParams) => {
        // Invalidate open orders query
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.CAMPAIGN_OPEN_ORDERS],
        });
        // Invalidate last computed event query
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.LAST_COMPUTED_EVENT],
        });

        // Invalidate referral queries (wait 3 seconds for indexing)
        setTimeout(async () => {
          await invalidateReferralEligibilityQueries(queryClient);
        }, 3_000);

        if (isTradeExecutedReportingEnabled) {
          const { order, orderParams, exchangeId, exchangeUserId, baseFiatAmount, orderBookComputedValues } = params;

          const mixpanelPayload = {
            id: order.id,
            exchangeId: exchangeId || "unknown", // it should be defined
            exchangeUserId,
            campaignName: campaignName,
            campaignType: campaignMetadata?.visibility || "unknown",
            campaignCategory: "trade-to-earn",
            epochNumber: currentEpochIndex,
            pairSymbol: order.pairSymbol,
            expectedPrice: orderParams.price,
            amount: orderParams.amount,
            type: order.type,
            side: order.side,
            status: order.status,
            timestamp: order.timestamp,
            marketType: "spot", // for now we only support spot trades for CEXs
            fromTradePage: false,
            tradingMethod: TradeMethod.HUB,
            price: order.price,
            baseAmount: order.amount,
            baseFiatAmount: baseFiatAmount || "unknown",
            walletAddress: hubUser?.defiAddress ?? undefined,
            chain: "offchain",
            ...(orderBookComputedValues || {}),
            ...referralProperties,
          } satisfies TradeExecutedPayload;
          trackEvent(METRICS_EVENTS.TRADE_EXECUTED, mixpanelPayload);
        }
      };
      config.onTradeFailed = (params: TradeFailedParams) => {
        const { orderParams, exchangeId, errorMessage } = params;

        const mixpanelPayload = {
          exchangeId: exchangeId || "unknown", // it should be defined
          campaignName: campaignName,
          campaignType: campaignMetadata?.visibility || "unknown",
          campaignCategory: "trade-to-earn",
          epochNumber: currentEpochIndex,
          pairSymbol: orderParams.pairSymbol,
          price: orderParams.price,
          amount: orderParams.amount,
          type: orderParams.orderType,
          side: orderParams.orderSide,
          baseAmount: orderParams.amount,
          marketType: "spot", // for now we only support spot trades for CEXs
          fromTradePage: false,
          tradingMethod: TradeMethod.HUB,
          walletAddress: hubUser?.defiAddress ?? undefined,
          errorMessage: errorMessage || "unknown error", // it should be defined
          chain: "offchain",
          ...referralProperties,
        } satisfies TradeFailedPayload;
        trackEvent(METRICS_EVENTS.TRADE_FAILED, mixpanelPayload);
      };

      let tradingUneligibleData: ITradingUneligibleData | undefined;

      if (!access && reason) {
        tradingUneligibleData = {
          invalidAccessReason: reason,
          defiAddresses,
          nextAccessDate,
          walletAgeReason,
          referralOnlyExchangeIds: campaignMetadata?.referralOnlyExchangeIds ?? undefined,
          campaignName: campaignName as string,
          blockedExchangeIds: effectiveEpoch?.exchangeIds ?? undefined,
        };
      }

      config.campaignMetadata = campaignMetadata;

      const result = {
        config,
        theme: {
          primaryColor: "var(--color-background-default)",
        },
        tradingUneligibleData,
      };

      return result;
    }

    return null;
  }, [
    authenticated,
    campaignMetadata,
    currentEpochIndex,
    campaignName,
    hubUser?.defiAddress,
    hubUser?.isWhiteListed,
    access,
    reason,
    isAccessLoading,
    nextAccessDate,
    logout,
    effectiveEpoch?.exchangeIds,
    effectiveEpoch?.limitOrder,
    defiAddresses,
    auth,
    requiresSocialAccounts,
    hasSocialAccountsConnected,
    onConnectSocialAccounts,
    refereeReferral?.referrerId,
    refereeReferral?.status,
    walletAgeReason,
    activePairSymbol?.pairSymbol,
    isTradeExecutedReportingEnabled,
  ]);

  return {
    tradeWidgetConfig,
    campaignMetadata,
    currentEpochIndex,
    campaignName,
  };
}
